import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

type DetailPlanData = {
    id: number;
    name: string;
    desc: string;
    price: string;
    currency: string;
    interest_rate: string;
    duration: string;
    commission: string;
    return_period: string;
    remain_slots: number;
    slots: number;
    interest: string[];
    policys: string[];
    clauses: string[];
};

const contract_template = `HỢP ĐỒNG HỢP TÁC ĐẦU TƯ - GÓI PIONEER
Bên A: CÔNG TY [TÊN CÔNG TY]
Bên B: [Họ và tên đối tác]

ĐIỀU 1: MỤC ĐÍCH HỢP ĐỒNG
Bên B tham gia hợp tác đầu tư vào Dự án với gói PIONEER, giá trị 1.500.000.000 VND, nhận quyền lợi 1 phần trong quỹ chia sẻ.

ĐIỀU 2: QUYỀN VÀ NGHĨA VỤ CỦA BÊN B
- Thanh toán phí nhận quyền lợi: 1.500.000.000 VND
- Nhận phần chia sẻ từ 8% quỹ chia sẻ trên tổng doanh thu ròng
- Mức chia sẻ tối thiểu: 3%/giá trị tham gia/tháng
- Tuân thủ các quy định của Dự án

ĐIỀU 3: QUYỀN VÀ NGHĨA VỤ CỦA BÊN A
- Đảm bảo quyền lợi cho Bên B theo thỏa thuận
- Thanh toán phần chia sẻ đầy đủ, đúng hạn
- Cung cấp báo cáo doanh thu và phân bổ minh bạch

ĐIỀU 4: THỜI HẠN HỢP ĐỒNG
Hợp đồng có hiệu lực từ ngày ký đến khi Dự án kết thúc hoặc theo thỏa thuận chấm dứt.

Ngày ký: [Ngày tháng năm]
Chữ ký Bên B: [Chữ ký]
`;

const detailPlanData: DetailPlanData = {
    id: 1,
    name: "TITAN",
    desc: "Gói đầu tư cao cấp với quyền lợi 6 phần trong quỹ chia sẻ",
    price: "300.000.000",
    currency: "VND",
    interest_rate: "0.228571428571%",
    duration: "3650 ngày",
    commission: "0.228571428571%",
    return_period: "Hàng tháng",
    remain_slots: 5,
    slots: 20,
    interest: [
        "Quyền lợi: 1 phần trong quỹ chia sẻ",
        "Tỷ lệ chia sẻ: ≈ 0,038095238095% trên doanh thu ròng trong kỳ (khi đủ 60 đối tác)",
        "Mức chia sẻ tối thiểu: 3%/giá trị tham gia/tháng",
        "Thời hạn: Trong suốt thời gian Dự án tạo doanh thu",
    ],
    policys: [
        "Chính sách hoàn trả trước hạn: Có thể hoàn trả trước hạn với mức phí 2% trên giá trị tham gia.",
        "Chính sách bảo mật thông tin: Cam kết bảo mật thông tin cá nhân và giao dịch của đối tác.",
        "Chính sách hỗ trợ đối tác: Hỗ trợ tư vấn và giải đáp thắc mắc liên quan đến kế hoạch đầu tư.",
    ],
    clauses: [
        "Điều khoản tham gia: Đối tác tham gia kế hoạch đầu tư phải tuân thủ các điều khoản và điều kiện đã được quy định.",
        "Điều khoản rủi ro: Đối tác cần hiểu rõ về các rủi ro liên quan đến kế hoạch đầu tư và chấp nhận chịu trách nhiệm về quyết định đầu tư của mình.",
        "Điều khoản thay đổi kế hoạch: Công ty có quyền thay đổi các điều khoản và điều kiện của kế hoạch đầu tư sau khi thông báo trước cho đối tác.",
    ]
};

const DetailPlanTabs: React.FC<{ listData: string[] }> = ({ listData }) => {
    return (
        <div className="ml-5">
            <ul className="list-decimal pl-5 pr-5 space-y-2">
                {listData.map((item, index) => (
                    <li key={index} className="text-justify text-base text-gray-700 dark:text-gray-300">
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
};

interface DetailPlanProps {
    id: number;
    next(): void;
    back(): void;
}

const DetailPlan: React.FC<DetailPlanProps> = ({ id, next, back }) => {
    const [showModal, setShowModal] = React.useState<boolean>(false);
    useEffect(() => {
        console.log("Detail Plan ID:", id);
    }, [id]);
    const [selectedTab, setSelectedTab] = React.useState<string>('1');
    const tabItems = [
        {
            key: '1',
            label: `Quyền lợi`,
            children: <DetailPlanTabs listData={detailPlanData.interest} />,
        },
        {
            key: '2',
            label: `Chính sách`,
            children: <DetailPlanTabs listData={detailPlanData.policys} />,
        },
        {
            key: '3',
            label: `Điều khoản`,
            children: <DetailPlanTabs listData={detailPlanData.clauses} />,
        },
    ];

    // Modal Portal
    const modalPortal = showModal && createPortal(
        <div className="fixed inset-0 z-[9999]">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 z-[9998]"
                onClick={() => setShowModal(false)}
            ></div>

            {/* Modal Content */}
            <div className="
                fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                z-[9999]
                bg-white dark:bg-gray-800
                rounded-2xl
                w-[90%] max-w-3xl min-w-[300px]
                max-h-[90vh]
                flex flex-col
                shadow-2xl
            ">
                {/* Header */}
                <div className="
                    bg-gradient-to-r from-slate-700 to-slate-800
                    px-8 py-6
                    text-white
                    flex items-center justify-between
                    rounded-t-2xl
                ">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-lg">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                <path d="M10 9H8"></path>
                                <path d="M16 13H8"></path>
                                <path d="M16 17H8"></path>
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">Mẫu hợp đồng</h3>
                            <p className="text-slate-300 text-sm">PIONEER</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        aria-label="Close contract modal"
                        onClick={() => setShowModal(false)}
                        className="w-10 h-10 hover:bg-white/10 rounded-xl flex items-center justify-center transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                            <path d="M18 6 6 18"></path>
                            <path d="m6 6 12 12"></path>
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900 dark:to-gray-800">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-inner">
                        <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-mono">
                            {contract_template}
                        </pre>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex gap-4 rounded-b-2xl">
                    <button
                        type="button"
                        className="flex-1 py-3.5 px-6 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-white font-semibold rounded-xl transition-all border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                        onClick={() => setShowModal(false)}
                    >
                        Đóng
                    </button>
                    <button
                        type="button"
                        className="flex-1 py-3.5 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl"
                    >
                        Đồng ý và tiếp tục
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );

    return (
        <>
            {modalPortal}
            <div className="
                flex flex-col gap-5
                my-5 mx-auto
                w-[calc(60%-40px)]
                bg-white dark:bg-gray-900
                p-5
            ">
                {/* Header */}
                <div>
                    <span className="flex items-center gap-2.5">
                        <div>
                            <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                                {detailPlanData.name}
                            </h2>
                            <p className="text-base text-gray-500 dark:text-gray-400 mt-1">
                                {detailPlanData.desc}
                            </p>
                        </div>
                        <div className="ml-auto text-sm text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5 rounded-md font-bold">
                            <span>{detailPlanData.remain_slots}</span> /
                            <span>{detailPlanData.slots}</span> slots remaining
                        </div>
                    </span>

                    {/* Info Section */}
                    <div className="flex flex-row items-center justify-between mt-4 w-full">
                        <div className="flex flex-col items-start justify-center">
                            <div className="text-sm mb-1">Price</div>
                            <div className="flex items-baseline gap-4">
                                <span className="text-5xl font-bold text-blue-600 dark:text-cyan-400">
                                    {detailPlanData.price}
                                </span>
                                <span className="text-xl text-gray-500 dark:text-gray-400">
                                    {detailPlanData.currency}
                                </span>
                            </div>
                        </div>

                        {/* Duration Grid */}
                        <div className="mt-2.5 text-base grid grid-cols-2 gap-2.5 justify-items-start items-center">
                            <div className="w-40 border border-gray-200 dark:border-gray-700 px-4 py-2.5 rounded-md text-gray-700 dark:text-gray-300 flex flex-col justify-end items-end">
                                <span className="font-bold text-right">Return period</span>
                                <span className="text-gray-500 dark:text-gray-400 text-right">{detailPlanData.return_period}</span>
                            </div>
                            <div className="w-40 border border-gray-200 dark:border-gray-700 px-4 py-2.5 rounded-md text-gray-700 dark:text-gray-300 flex flex-col justify-end items-end">
                                <span className="font-bold text-right">Interest Rate</span>
                                <span className="text-gray-500 dark:text-gray-400 text-right">{detailPlanData.interest_rate}</span>
                            </div>
                            <div className="w-40 border border-gray-200 dark:border-gray-700 px-4 py-2.5 rounded-md text-gray-700 dark:text-gray-300 flex flex-col justify-end items-end">
                                <span className="font-bold text-right">Duration</span>
                                <span className="text-gray-500 dark:text-gray-400 text-right">{detailPlanData.duration}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs Section */}
                <div className="mt-5 border border-gray-200 dark:border-gray-700 rounded-2xl">
                    {/* Tab Headers */}
                    <div className="text-xl font-bold mb-5 grid grid-cols-3">
                        {tabItems.map((tab) => (
                            <div
                                onClick={() => setSelectedTab(tab.key)}
                                key={tab.key}
                                className={`
                                    px-4 py-2.5
                                    text-center
                                    cursor-pointer
                                    border-b-2
                                    ${selectedTab === tab.key
                                        ? 'border-blue-600 dark:border-blue-500 font-bold text-gray-900 dark:text-white'
                                        : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                                    }
                                `}
                            >
                                {tab.label}
                            </div>
                        ))}
                    </div>
                    {/* Tab Content */}
                    <div className="pb-4">
                        {tabItems.find(tab => tab.key === selectedTab)?.children}
                    </div>
                </div>

                {/* Review Contract */}
                <div className="flex flex-row items-center justify-between p-5 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
                    <div>
                        <h3 className="m-0 text-2xl font-bold text-gray-900 dark:text-white">
                            Contract template
                        </h3>
                        <p className="mt-1 mb-0 text-base text-gray-700 dark:text-gray-300">
                            You can review the contract template before joining the plan.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md cursor-pointer text-base font-bold transition-all hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                        Review Contract
                    </button>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center w-full gap-5">
                    <button
                        onClick={back}
                        className="w-1/2 h-11 px-5 py-2.5 bg-transparent border-2 border-gray-500 dark:border-gray-600 text-gray-500 dark:text-gray-400 rounded-xl cursor-pointer text-base transition-all hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                    >
                        Quay lại
                    </button>
                    <button
                        onClick={next}
                        className="w-[calc(50%+2px)] h-12 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl cursor-pointer text-base transition-all hover:from-blue-700 hover:to-blue-500"
                    >
                        Tham gia ngay
                    </button>
                </div>
            </div>
        </>
    );
};

export default DetailPlan;
